import { callGateway, type CallerIdentity } from "./gateway";
import { config } from "../config";
import { ApiError } from "./errors";

/**
 * Upstream agreements (owner OB-9/OB-14/OB-15, 2026-10-28; radaccess v1.193,
 * tables from owner SQL 259): what a TOP-LEVEL provider buys from its outside
 * bandwidth supplier, and the supplier's tax invoices (input GST).
 *
 *   GET  /access/operator/{ns}/upstream/agreements                 list, open first
 *   POST /access/operator/{ns}/upstream/agreements                 record one
 *   PUT  /access/operator/{ns}/upstream/agreements/{id}            change an open one
 *   PUT  /access/operator/{ns}/upstream/agreements/{id}/end        end it (never deleted)
 *   GET  /access/operator/{ns}/upstream/agreements/{id}/bills      its supplier bills
 *   POST /access/operator/{ns}/upstream/agreements/{id}/bills      record a bill
 *   PUT  /access/operator/{ns}/upstream/bills/{billId}/payment     what was paid
 *   PUT  /access/operator/{ns}/upstream/bills/{billId}/void        void it (never deleted)
 *
 * 🔑 Owner, Admin and Billing write; superadmin reads (RBAC "upstream").
 * Money travels as integer paise; the screen shows and takes rupees.
 */

const enc = (s: string) => encodeURIComponent(s);
const base = (ns: string) => `/operator/${enc(ns)}/upstream`;

export type ChargeBasis = "fixed" | "usage" | "both";
/** How the usage part is paid: PINs bought from the supplier, or a prepaid balance (funds). */
export type UsagePaidBy = "pins" | "funds";
export type BillingCycle = "monthly" | "quarterly" | "half_yearly" | "annual";
export type BandwidthUnit = "Mbps" | "Gbps";

export interface UpstreamAgreement {
  id: number;
  namespace: string;
  supplierNamespace?: string;
  supplierLegalName: string;
  supplierGstin?: string;
  supplierStateCode?: string;
  supplierLicenseType?: string;
  supplierLicenseNumber?: string;
  agreementRef?: string;
  chargeBasis: ChargeBasis;
  /** Set for usage and fixed-plus-usage; absent for fixed (radaccess v1.197, owner SQL 272). */
  usagePaidBy?: UsagePaidBy;
  /** Always Mbps (1 Gbps = 1000); bandwidthUnit is how it was agreed and is shown. */
  committedMbps: number;
  bandwidthUnit: BandwidthUnit;
  /** Per billing cycle, as quoted: before GST, or including it when priceIncludesGst. */
  commitmentPaise: number;
  priceIncludesGst: boolean;
  usageTerms?: string;
  gstRateBp: number;
  billingCycle: BillingCycle;
  termStart: string;
  termEnd?: string;
  status: "draft" | "active" | "ended";
  endedAt?: string;
  endReason?: string;
  note?: string;
  createdDate: string;
}

export type UpstreamAgreementInput = Omit<UpstreamAgreement, "id" | "namespace" | "status" | "endedAt" | "endReason" | "createdDate">;

export interface UpstreamBill {
  id: number;
  agreementId: number;
  supplierInvoiceNumber: string;
  invoiceDate: string;
  periodFrom?: string;
  periodTo?: string;
  taxablePaise: number;
  igstPaise: number;
  cgstPaise: number;
  sgstPaise: number;
  totalPaise: number;
  itcClaimable: boolean;
  paymentStatus: "unpaid" | "part" | "paid";
  paidPaise: number;
  paidAt?: string;
  paymentRef?: string;
  status: "recorded" | "void";
  voidReason?: string;
  note?: string;
  createdDate: string;
}

export interface UpstreamBillInput {
  supplierInvoiceNumber: string;
  invoiceDate: string;
  periodFrom?: string;
  periodTo?: string;
  taxablePaise: number;
  igstPaise: number;
  cgstPaise: number;
  sgstPaise: number;
  totalPaise: number;
  itcClaimable: boolean;
  note?: string;
}

export const LICENCE_TYPES = ["UL", "ISP_A", "ISP_B", "ISP_C", "VNO"] as const;

export const CYCLE_LABEL: Record<BillingCycle, { name: string; per: string }> = {
  monthly: { name: "Monthly", per: "a month" },
  quarterly: { name: "Quarterly", per: "a quarter" },
  half_yearly: { name: "Half-yearly", per: "a half-year" },
  annual: { name: "Annually", per: "a year" },
};

/** The usual GST slabs; 18% is what connectivity is charged at. */
export const GST_RATES = [0, 5, 12, 18, 28] as const;

/**
 * Bandwidth as typed in its unit, to the whole Mbps the record keeps:
 * "2.5" Gbps is 2500 Mbps. Null when it is not a positive amount, or when it
 * does not come to a whole Mbps.
 */
export function toMbps(text: string, unit: BandwidthUnit): number | null {
  const t = text.trim();
  if (!/^\d{1,7}(\.\d{1,3})?$/.test(t)) return null;
  const [whole, frac = ""] = t.split(".");
  if (unit === "Mbps" && frac.replace(/0+$/, "") !== "") return null;
  const mbps = unit === "Gbps" ? Number(whole) * 1000 + Number(frac.padEnd(3, "0")) : Number(whole);
  return mbps > 0 ? mbps : null;
}

/** The record's Mbps back in the unit it was agreed in: "2.5 Gbps", "100 Mbps". */
export function formatBandwidth(mbps: number, unit: BandwidthUnit): string {
  return unit === "Gbps" ? `${String(mbps / 1000)} Gbps` : `${mbps} Mbps`;
}

/** The number a bandwidth field holds for an agreement being edited. */
export function bandwidthText(mbps: number, unit: BandwidthUnit): string {
  return unit === "Gbps" ? String(mbps / 1000) : String(mbps);
}

export const USAGE_PAID_BY_LABEL: Record<UsagePaidBy, string> = {
  pins: "PINs",
  funds: "Funds (prepaid balance)",
};

/** "By usage, paid by PINs"; a fixed commitment has no usage to pay. */
export function chargeText(basis: ChargeBasis, paidBy?: UsagePaidBy): string {
  return basis === "fixed" || !paidBy ? CHARGE_BASIS_LABEL[basis] : `${CHARGE_BASIS_LABEL[basis]}, paid by ${USAGE_PAID_BY_LABEL[paidBy]}`;
}

export const CHARGE_BASIS_LABEL: Record<ChargeBasis, string> = {
  fixed: "Fixed commitment (per billing cycle)",
  usage: "By usage",
  both: "Fixed commitment plus usage",
};

export const listAgreements = async (i: CallerIdentity, ns: string, signal?: AbortSignal) =>
  (await callGateway<UpstreamAgreement[]>(i, config().gatewayBase, `${base(ns)}/agreements`, signal ? { signal } : {})).data ?? [];

export const createAgreement = async (i: CallerIdentity, ns: string, a: UpstreamAgreementInput) =>
  (await callGateway<UpstreamAgreement>(i, config().gatewayBase, `${base(ns)}/agreements`, { method: "POST", body: a })).data;

export const updateAgreement = async (i: CallerIdentity, ns: string, id: number, a: UpstreamAgreementInput) =>
  (await callGateway<UpstreamAgreement>(i, config().gatewayBase, `${base(ns)}/agreements/${id}`, { method: "PUT", body: a })).data;

export const endAgreement = async (i: CallerIdentity, ns: string, id: number, reason: string) =>
  (await callGateway<UpstreamAgreement>(i, config().gatewayBase, `${base(ns)}/agreements/${id}/end`, { method: "PUT", body: { reason } })).data;

export const listBills = async (i: CallerIdentity, ns: string, agreementId: number, signal?: AbortSignal) =>
  (await callGateway<UpstreamBill[]>(i, config().gatewayBase, `${base(ns)}/agreements/${agreementId}/bills`, signal ? { signal } : {})).data ?? [];

export const recordBill = async (i: CallerIdentity, ns: string, agreementId: number, b: UpstreamBillInput) =>
  (await callGateway<UpstreamBill>(i, config().gatewayBase, `${base(ns)}/agreements/${agreementId}/bills`, { method: "POST", body: b })).data;

export const setBillPayment = async (i: CallerIdentity, ns: string, billId: number, p: { paidPaise: number; paidAt: string; paymentRef: string }) =>
  (await callGateway<UpstreamBill>(i, config().gatewayBase, `${base(ns)}/bills/${billId}/payment`, { method: "PUT", body: p })).data;

export const voidBill = async (i: CallerIdentity, ns: string, billId: number, reason: string) =>
  (await callGateway<UpstreamBill>(i, config().gatewayBase, `${base(ns)}/bills/${billId}/void`, { method: "PUT", body: { reason } })).data;

/**
 * Rupees as typed ("10,000.50") to integer paise; null when it is not an
 * amount. At most two decimals -- a third would be a rounding the supplier's
 * invoice never made.
 */
export function rupeesToPaise(text: string): number | null {
  const t = text.replace(/,/g, "").trim();
  if (!/^\d{1,11}(\.\d{1,2})?$/.test(t)) return null;
  const [whole, frac = ""] = t.split(".");
  return Number(whole) * 100 + Number(frac.padEnd(2, "0"));
}

/** Paise back to the plain rupee text a form field holds. */
export function paiseToRupees(paise: number): string {
  const whole = Math.trunc(paise / 100);
  const frac = paise % 100;
  return frac === 0 ? String(whole) : `${whole}.${String(frac).padStart(2, "0")}`;
}

/**
 * The GST on a taxable value, split the way the supplier's invoice will be:
 * IGST across states, equal CGST and SGST within one. An odd paisa goes to
 * neither half unevenly -- radaccess refuses CGST != SGST -- so the split is
 * floored to an even total, which is what the supplier's invoice prints.
 */
export function gstSplit(taxablePaise: number, rateBp: number, interState: boolean): { igst: number; cgst: number; sgst: number; total: number } {
  const tax = Math.round((taxablePaise * rateBp) / 10000);
  if (interState) return { igst: tax, cgst: 0, sgst: 0, total: taxablePaise + tax };
  const half = Math.floor(tax / 2);
  return { igst: 0, cgst: half, sgst: half, total: taxablePaise + 2 * half };
}

/**
 * The split for a GST-inclusive total: the taxable value whose GST, split as
 * gstSplit splits it, adds back to exactly that total. Null when no taxable
 * value does (then the invoice's printed taxable value has to be typed).
 */
export function fromInclusive(totalPaise: number, rateBp: number, interState: boolean): { taxable: number; igst: number; cgst: number; sgst: number; total: number } | null {
  const guess = Math.round((totalPaise * 10000) / (10000 + rateBp));
  for (const t of [guess, guess - 1, guess + 1, guess - 2, guess + 2]) {
    if (t < 0) continue;
    const s = gstSplit(t, rateBp, interState);
    if (s.total === totalPaise) return { taxable: t, ...s };
  }
  return null;
}

/** Whether supplier and buyer are in different states: the GSTINs' first two digits. */
export function isInterState(supplierGstin: string | undefined, buyerStateCode: string | undefined): boolean | null {
  const s = supplierGstin?.slice(0, 2);
  if (!s || !buyerStateCode) return null;
  return s !== buyerStateCode;
}

/** radaccess's refusal, in words; its 422 already carries the sentence. */
export function upstreamRefusal(e: unknown): string {
  if (e instanceof ApiError) {
    if (e.status === 503) return "Upstream agreements are not switched on yet.";
    if (e.status === 403) return "Refused: only the Owner, Admin or Billing staff record upstream agreements.";
    return e.message;
  }
  return e instanceof Error ? e.message : String(e);
}
